import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { MAP_SIZE, MAP_AREAS, type AreaId, type MapArea } from "../../content/map";
import { AreaNode } from "./AreaNode";
import { RouteLayer } from "./RouteLayer";
import { PlayerMapPawn } from "./PlayerMapPawn";
import { FogOfWarLayer } from "./FogOfWarLayer";
import { AmbientAtmosphere } from "./AmbientAtmosphere";

const MIN_SCALE = 0.35;
const MAX_SCALE = 1.6;
const TAP_THRESHOLD = 8; // この移動量(px)を超えたらドラッグ扱いでタップ無効
const NODE_CLEAR_RADIUS = 92;

type View = { tx: number; ty: number; scale: number };

// 固定キャンバス(MAP_SIZE)を transform でパン/ズームして表示する。
// PC: ホイールズーム＋ドラッグ。タブレット/スマホ: ピンチズーム＋ドラッグ。
import type { AreaSummary } from "../../content/lesson";

export function MapView({
  onAreaTap,
  isUnlocked,
  currentAreaId,
  progressByArea,
  areas = [],
  onCourseTap,
  newlyUnlockedAreaId,
  onClearSequenceEnd,
}: {
  onAreaTap: (a: MapArea) => void;
  onCourseTap: (areaId: AreaId, courseIndex: number) => void;
  isUnlocked: (a: MapArea) => boolean;
  currentAreaId: AreaId;
  progressByArea: Partial<Record<AreaId, { done: number; total: number; cleared: boolean }>>;
  areas?: AreaSummary[];
  newlyUnlockedAreaId?: AreaId | null;
  onClearSequenceEnd?: () => void;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>({ tx: 0, ty: 0, scale: 0.5 });
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<{ dist: number } | null>(null);
  const movedRef = useRef(0);

  const clamp = useCallback((v: View): View => {
    const el = viewportRef.current;
    const vw = el?.clientWidth ?? 0;
    const vh = el?.clientHeight ?? 0;
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale));
    const cw = MAP_SIZE.width * scale;
    const ch = MAP_SIZE.height * scale;
    const tx = cw <= vw ? (vw - cw) / 2 : Math.min(0, Math.max(vw - cw, v.tx));
    const ty = ch <= vh ? (vh - ch) / 2 : Math.min(0, Math.max(vh - ch, v.ty));
    return { tx, ty, scale };
  }, []);

  // 初期表示：ステージ盤面全体が見える倍率にする。
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const fit = Math.min(
      el.clientWidth / MAP_SIZE.width,
      el.clientHeight / MAP_SIZE.height,
    );
    setView(clamp({ tx: 0, ty: 0, scale: fit }));
  }, [clamp]);

  function localPoint(e: { clientX: number; clientY: number }) {
    const r = viewportRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  // (px,py) を固定点としてズームする
  function zoomAt(px: number, py: number, factor: number) {
    setView((v) => {
      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale * factor));
      const k = scale / v.scale;
      return clamp({
        tx: px - (px - v.tx) * k,
        ty: py - (py - v.ty) * k,
        scale,
      });
    });
  }

  function onPointerDown(e: React.PointerEvent) {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, localPoint(e));
    movedRef.current = 0;
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchRef.current = { dist: Math.hypot(a.x - b.x, a.y - b.y) };
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    const cur = localPoint(e);
    pointers.current.set(e.pointerId, cur);

    if (pointers.current.size === 2 && pinchRef.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const cx = (a.x + b.x) / 2;
      const cy = (a.y + b.y) / 2;
      movedRef.current = Infinity; // ピンチ中はタップ無効
      zoomAt(cx, cy, dist / pinchRef.current.dist);
      pinchRef.current = { dist };
      return;
    }

    const dx = cur.x - prev.x;
    const dy = cur.y - prev.y;
    movedRef.current += Math.abs(dx) + Math.abs(dy);
    setView((v) => clamp({ ...v, tx: v.tx + dx, ty: v.ty + dy }));
  }

  function onPointerUp(e: React.PointerEvent) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchRef.current = null;
    if (pointers.current.size === 0) movedRef.current = 0;
  }

  function onWheel(e: React.WheelEvent) {
    setView((v) => clamp({ ...v, ty: v.ty - e.deltaY }));
  }

  function handleTap(area: MapArea) {
    if (movedRef.current > TAP_THRESHOLD) return; // ドラッグ後の誤タップを除外
    onAreaTap(area);
  }

  function zoomButton(factor: number) {
    const el = viewportRef.current;
    if (!el) return;
    zoomAt(el.clientWidth / 2, el.clientHeight / 2, factor);
  }

  // 新規エリア解放時のスムーズカメラパン
  useLayoutEffect(() => {
    if (!newlyUnlockedAreaId) return;
    const targetArea = MAP_AREAS.find((a) => a.id === newlyUnlockedAreaId);
    const el = viewportRef.current;
    if (!targetArea || !el) return;

    const vw = el.clientWidth;
    const vh = el.clientHeight;
    const targetScale = Math.min(1.0, Math.max(0.65, view.scale));
    const targetTx = vw / 2 - targetArea.position.x * targetScale;
    const targetTy = vh / 2 - targetArea.position.y * targetScale;

    setView(clamp({ tx: targetTx, ty: targetTy, scale: targetScale }));
  }, [newlyUnlockedAreaId, clamp]);

  return (
    <div
      ref={viewportRef}
      className="relative w-full h-full overflow-hidden touch-none select-none cursor-grab active:cursor-grabbing"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
    >
      <div
        className="absolute top-0 left-0 origin-top-left"
        style={{
          width: MAP_SIZE.width,
          height: MAP_SIZE.height,
          transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`,
        }}
      >
        <StageBoardBackdrop />
        {/* 自然の息遣い（雲・波・桜の花びら） */}
        <AmbientAtmosphere />
        {/* 未解放エリアを包む雲霧レイヤーと霧晴れ演出 */}
        <FogOfWarLayer
          isUnlocked={isUnlocked}
          newlyUnlockedAreaId={newlyUnlockedAreaId}
          onClearSequenceEnd={onClearSequenceEnd}
        />
        <RouteLayer
          progressByArea={progressByArea}
          isUnlocked={(id) => {
            const area = MAP_AREAS.find((a) => a.id === id);
            return area ? isUnlocked(area) : false;
          }}
          onCourseTap={onCourseTap}
        />
        {(() => {
          const maxUnlockedOrder = Math.max(
            -1,
            ...MAP_AREAS.filter((a) => isUnlocked(a)).map((a) => a.unlockOrder),
          );
          const unlockConditionByArea = Object.fromEntries(
            areas.map((a) => [a.id, a.unlock_condition ?? null]),
          );

          return MAP_AREAS.map((a) => {
            const locked = !isUnlocked(a);
            const isImmediateNextLocked = locked && a.unlockOrder === maxUnlockedOrder + 1;
            return (
              <div key={a.id}>
                <AreaNode
                  area={a}
                  locked={locked}
                  isImmediateNextLocked={isImmediateNextLocked}
                  unlockCondition={unlockConditionByArea[a.id]}
                  active={a.id === currentAreaId}
                  progress={progressByArea[a.id]}
                  progressByArea={progressByArea}
                  onTap={handleTap}
                />
              </div>
            );
          });
        })()}

      </div>

      {/* ズームボタン（ビューポート固定） */}
      <div className="absolute bottom-5 right-5 flex flex-col gap-2">
        <ZoomButton label="＋" onClick={() => zoomButton(1.25)} />
        <ZoomButton label="－" onClick={() => zoomButton(1 / 1.25)} />
      </div>

      {/* プレイヤー着せ替えアバター駒：現在地マスの真上にちょこんと乗ってトコトコナビゲート */}
      {(() => {
        const piece = getPiecePosition(currentAreaId, progressByArea);
        if (!piece) return null;
        const pawnX = piece.x * view.scale + view.tx;
        const pawnY = piece.y * view.scale + view.ty;
        const currentArea = MAP_AREAS.find((a) => a.id === currentAreaId);
        const unlocked = currentArea ? isUnlocked(currentArea) : false;
        return (
          <PlayerMapPawn
            screenX={pawnX}
            screenY={pawnY}
            mapScale={view.scale}
            currentAreaName={currentArea?.nameJa ?? "ステージ"}
            isUnlocked={unlocked}
            onPawnTap={() => currentArea && handleTap(currentArea)}
          />
        );
      })()}
    </div>
  );
}

function getPiecePosition(
  currentAreaId: AreaId,
  progressByArea: Partial<Record<AreaId, { done: number; total: number; cleared: boolean }>>,
) {
  const area = MAP_AREAS.find((a) => a.id === currentAreaId);
  if (!area) return null;
  const nextId = area.connectsTo[0];
  if (!nextId) return area.position;

  const next = MAP_AREAS.find((a) => a.id === nextId);
  if (!next) return area.position;

  const progress = progressByArea[area.id];
  const ratio = progress?.cleared
    ? 1
    : Math.min(1, Math.max(0, (progress?.done ?? 0) / Math.max(1, progress?.total ?? 1)));
  const stoneTotal = Math.max(1, progress?.total ?? 5);
  const samples = buildRouteSamples(area.position, next.position, area.unlockOrder, stoneTotal);
  return pointAtRouteProgress(samples, Math.min(0.98, ratio));
}

function buildRouteSamples(
  a: { x: number; y: number },
  b: { x: number; y: number },
  idx: number,
  stoneTotal: number,
) {
  return stoneTotal >= 15
    ? sampleSineRoute(a, b, idx, stoneTotal, 144)
    : sampleQuadraticRoute(a, b, idx, stoneTotal, 96);
}

function sampleQuadraticRoute(
  a: { x: number; y: number },
  b: { x: number; y: number },
  idx: number,
  _stoneTotal: number,
  steps: number,
) {
  const segAngle = Math.atan2(b.y - a.y, b.x - a.x);
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const perp = Math.min(Math.max(len * 0.14, 44), 96);
  const side = idx % 2 === 0 ? 1 : -1;
  const c = {
    x: (a.x + b.x) / 2 + Math.sin(segAngle) * perp * side,
    y: (a.y + b.y) / 2 - Math.cos(segAngle) * perp * side,
  };
  return sampleParametric(steps, (t) => pointOnQuadratic(a, c, b, t));
}

function sampleSineRoute(
  a: { x: number; y: number },
  b: { x: number; y: number },
  idx: number,
  _stoneTotal: number,
  steps: number,
) {
  const segAngle = Math.atan2(b.y - a.y, b.x - a.x);
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const density = Math.min(1, Math.max(0, (_stoneTotal - 10) / 10));
  const waves = 1 + density * 0.5;
  const side = idx % 2 === 0 ? 1 : -1;
  const ux = Math.cos(segAngle);
  const uy = Math.sin(segAngle);
  const nx = Math.sin(segAngle) * side;
  const ny = -Math.cos(segAngle) * side;
  const amplitude = Math.min(Math.max(len * (0.10 + density * 0.08), 48), 120);
  return sampleParametric(steps, (t) => {
    const taper = Math.sin(Math.PI * t);
    const wave = Math.sin(Math.PI * 2 * waves * t);
    return {
      x: a.x + ux * len * t + nx * amplitude * taper * wave,
      y: a.y + uy * len * t + ny * amplitude * taper * wave,
    };
  });
}

function sampleParametric(
  steps: number,
  pointAt: (t: number) => { x: number; y: number },
) {
  let distance = 0;
  let prev = pointAt(0);
  const samples = [{ ...prev, distance }];
  for (let i = 1; i <= steps; i += 1) {
    const p = pointAt(i / steps);
    distance += Math.hypot(p.x - prev.x, p.y - prev.y);
    samples.push({ ...p, distance });
    prev = p;
  }
  return samples;
}

function pointAtDistance(
  samples: Array<{ x: number; y: number; distance: number }>,
  target: number,
) {
  const end = samples[samples.length - 1];
  if (target <= 0) return samples[0];
  if (target >= end.distance) return end;

  for (let i = 1; i < samples.length; i += 1) {
    const curr = samples[i];
    if (curr.distance < target) continue;
    const prev = samples[i - 1];
    const segment = Math.max(1, curr.distance - prev.distance);
    const ratio = (target - prev.distance) / segment;
    return {
      x: prev.x + (curr.x - prev.x) * ratio,
      y: prev.y + (curr.y - prev.y) * ratio,
    };
  }
  return end;
}

function pointAtRouteProgress(
  samples: Array<{ x: number; y: number; distance: number }>,
  ratio: number,
) {
  const pathLength = samples[samples.length - 1].distance;
  const padding = Math.min(
    NODE_CLEAR_RADIUS,
    Math.max(48, pathLength * 0.22),
  );
  const startDistance = padding;
  const endDistance = Math.max(startDistance, pathLength - padding);
  return pointAtDistance(
    samples,
    startDistance + (endDistance - startDistance) * Math.min(1, Math.max(0, ratio)),
  );
}

function pointOnQuadratic(
  a: { x: number; y: number },
  c: { x: number; y: number },
  b: { x: number; y: number },
  t: number,
) {
  const u = 1 - t;
  return {
    x: u * u * a.x + 2 * u * t * c.x + t * t * b.x,
    y: u * u * a.y + 2 * u * t * c.y + t * t * b.y,
  };
}

function StageBoardBackdrop() {
  return (
    <>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, #fff8df 0%, #f4efd8 38%, #dcebdc 72%, #cfe6ed 100%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "linear-gradient(90deg, #7b8f5a 1px, transparent 1px), linear-gradient(#7b8f5a 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div
        className="absolute inset-[70px] rounded-[64px] border-[5px] border-[#d5b962]/35"
        style={{ boxShadow: "inset 0 0 80px rgba(255,255,255,0.38)" }}
      />
      <div className="absolute left-[28px]   top-[26px]   h-32 w-52  rounded-full bg-white/50 blur-2xl" />
      <div className="absolute left-[95px]   top-[6px]    h-24 w-80  rounded-full bg-[#fff6d8]/46 blur-xl" />
      <div className="absolute left-[1452px] top-[38px]   h-28 w-48  rounded-full bg-white/42 blur-2xl" />
      <div className="absolute left-[1368px] top-[16px]   h-20 w-72  rounded-full bg-[#fff6d8]/42 blur-xl" />
      <div className="absolute left-[700px]  top-[12px]   h-20 w-56  rounded-full bg-white/34 blur-2xl" />
      <div className="absolute left-[220px] bottom-[120px] h-52 w-[520px] rounded-full bg-[#9ccfdc]/18 blur-2xl" />
      <div className="absolute right-[220px] bottom-[140px] h-48 w-[500px] rounded-full bg-[#9ccf9a]/18 blur-2xl" />
      <BoardPattern />
    </>
  );
}

function BoardPattern() {
  return (
    <svg
      className="absolute left-0 top-0 pointer-events-none"
      width="1800"
      height="1360"
      viewBox="0 0 1800 1360"
      aria-hidden
    >
      <defs>
        <pattern id="soft-dots" width="120" height="120" patternUnits="userSpaceOnUse">
          <circle cx="24" cy="24" r="5" fill="#d5b962" opacity="0.18" />
          <circle cx="84" cy="70" r="3.5" fill="#3f6b4f" opacity="0.12" />
        </pattern>
        <linearGradient id="ribbon-a" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.44" />
          <stop offset="100%" stopColor="#f6d889" stopOpacity="0.18" />
        </linearGradient>
      </defs>
      <rect width="1800" height="1360" fill="url(#soft-dots)" />
      <path
        d="M120 250 C420 80 650 240 900 160 C1160 78 1370 150 1660 80"
        fill="none"
        stroke="url(#ribbon-a)"
        strokeWidth="70"
        strokeLinecap="round"
        opacity="0.72"
      />
      <path
        d="M80 1080 C360 1210 610 1030 860 1120 C1110 1210 1360 1120 1700 1240"
        fill="none"
        stroke="#ffffff"
        strokeWidth="62"
        strokeLinecap="round"
        opacity="0.28"
      />
      <g opacity="0.5">
        <path d="M156 184 l10 22 22 10-22 10-10 22-10-22-22-10 22-10z" fill="#d5b962" />
        <path d="M1510 230 l8 18 18 8-18 8-8 18-8-18-18-8 18-8z" fill="#c44738" />
        <path d="M330 1180 l7 16 16 7-16 7-7 16-7-16-16-7 16-7z" fill="#3f6b4f" />
        <path d="M1580 1050 l9 19 19 9-19 9-9 19-9-19-19-9 19-9z" fill="#d5b962" />
      </g>
      <g fill="none" stroke="#d5b962" strokeWidth="3" opacity="0.18">
        <circle cx="900" cy="680" r="560" />
        <circle cx="900" cy="680" r="410" />
      </g>
    </svg>
  );
}

function ZoomButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={(e) => e.stopPropagation()}
      className="w-12 h-12 rounded-xl backdrop-blur shadow-lg text-3xl font-bold flex items-center justify-center active:scale-95 transition border"
      style={{
        background: "rgba(240,228,196,0.96)",
        color: "#5A2810",
        borderColor: "rgba(160,116,8,0.40)",
      }}
    >
      {label}
    </button>
  );
}
